import React, { useEffect, useMemo } from 'react';
import { Spinner } from 'react-bootstrap';

import RadioButton from 'components/DesignSystem/RadioButton';
import DatePicker from 'components/DatePicker';
import Icon from 'components/DesignSystem/Icon';
import { ModelType } from 'components/Meteorology/Fields/ControlPanel/Comparator/modelAccordion';
import useComparatorControlPanel from 'utils/hooks/meteorology/comparator/useComparatorControlPanel';
import useAvailableDates from 'utils/hooks/meteorology/comparator/useAvailableDates';
import useControlPanelOptions from 'utils/hooks/meteorology/useControlPanelOptions';
import formatDateDifference from 'helper/formatDateDifference';
import dateDiffToDate from 'helper/dateDiffToDate';
import sortedDatesAsIntervals from 'helper/Shared/sortedDatesAsIntervals';
import { cssVar } from 'constants/theme';

import './index.scss';

export default function ModelStep({
  modelType,
  title,
  onBack,
}: {
  modelType: ModelType;
  title: string;
  onBack: () => void;
}) {
  const { models, getModelOptions, isFetching } = useControlPanelOptions('comparador');

  const {
    baseModel,
    confrontationalModel,
    setBaseModel,
    setConfrontationalModel,
    baseForecastDateDiff,
    confrontationalForecastDateDiff,
    setBaseForecastDateDiff,
    setConfrontationalForecastDateDiff,
    baseRuntime,
    confrontationalRuntime,
    setBaseRuntime,
    setConfrontationalRuntime,
    baseMember,
    confrontationalMember,
    setBaseMember,
    setConfrontationalMember,
    baseRMV,
    confrontationalRMV,
    setBaseRMV,
    setConfrontationalRMV,
    setTitle,
  } = useComparatorControlPanel();

  function titleClearer<T>(setter: (v: T) => void) {
    return (v: T) => {
      setTitle('', '');
      setter(v);
    };
  }

  const [
    model, setModel,
    dateDiff, setDateDiff,
    runtime, setRuntime,
    member, setMember,
    rmv, setRMV,
  ] = modelType === ModelType.BASE
    ? [
      baseModel, setBaseModel,
      baseForecastDateDiff, setBaseForecastDateDiff,
      baseRuntime, setBaseRuntime,
      baseMember, setBaseMember,
      baseRMV, setBaseRMV,
    ]
    : [
      confrontationalModel, setConfrontationalModel,
      confrontationalForecastDateDiff, setConfrontationalForecastDateDiff,
      confrontationalRuntime, setConfrontationalRuntime,
      confrontationalMember, setConfrontationalMember,
      confrontationalRMV, setConfrontationalRMV,
    ];

  const handleModel = (value: string) => {
    setTitle('', '');
    setModel(value);
  };
  const handleRuntime = titleClearer(setRuntime);
  const handleMember = titleClearer(setMember);
  const handleRMV = titleClearer(setRMV);

  const availableDates = useAvailableDates(model ?? '');
  const availableIntervals = useMemo(
    () => sortedDatesAsIntervals(availableDates),
    [availableDates],
  );

  const runtimeOptions = getModelOptions(model ?? '').runtimes;
  const memberOptions = getModelOptions(model ?? '').members;

  const date = dateDiffToDate(dateDiff, availableDates) || null;
  const handleDate = titleClearer((d: Date | null) => {
    setDateDiff(formatDateDifference(d, availableDates));
  });

  useEffect(() => {
    const runtimeIsAvailable = runtimeOptions.some((ro) => ro.id === runtime);
    if (!isFetching && !runtimeIsAvailable) setRuntime(undefined);
  }, [isFetching, runtime, runtimeOptions, setRuntime]);

  useEffect(() => {
    const memberIsAvailable = memberOptions.some((mo) => mo.id === member);
    if (!isFetching && !memberIsAvailable) setMember(undefined);
  }, [isFetching, member, memberOptions, setMember]);

  return (
    <div className="model-step">
      <button type="button" className="model-step-back" onClick={onBack}>
        <Icon iconType="left_arrow" color={cssVar('text-strong')} />
        <span>{title}</span>
      </button>

      {isFetching ? (
        <div className="text-center py-4"><Spinner /></div>
      ) : (
        <>
          <section className="model-step-section">
            <h4>Modelo</h4>
            <div className="model-step-options">
              {models.map((m) => (
                <RadioButton
                  key={m.value}
                  className="text-uppercase"
                  name={`model-step-model-${modelType}`}
                  label={m.label}
                  value={m.value}
                  checked={model === m.value}
                  onChange={() => handleModel(m.value)}
                />
              ))}
            </div>
          </section>

          <section className="model-step-section">
            <h4>Data de Previsão</h4>
            <DatePicker
              inline
              selected={date}
              onChange={handleDate}
              includeDateIntervals={availableIntervals}
            />
          </section>

          <section className="model-step-section">
            <h4>Runtime</h4>
            <div className="model-step-options">
              {runtimeOptions.map((ro) => (
                <RadioButton
                  key={ro.id}
                  label={ro.label}
                  value={ro.id}
                  checked={runtime === ro.id}
                  onChange={() => handleRuntime(ro.id)}
                />
              ))}
            </div>
          </section>

          <section className="model-step-section">
            <h4>Membro</h4>
            <div className="model-step-options">
              {memberOptions.map((mo) => (
                <RadioButton
                  key={mo.id}
                  label={mo.label}
                  value={mo.id}
                  checked={member === mo.id}
                  onChange={() => handleMember(mo.id)}
                />
              ))}
            </div>
          </section>

          <section className="model-step-section">
            <h4>RMV</h4>
            <div className="model-step-options">
              <RadioButton
                name={`model-step-rmv-${modelType}`}
                label="Com RMV"
                value="with-rmv"
                checked={rmv}
                onChange={() => handleRMV(true)}
              />
              <RadioButton
                name={`model-step-rmv-${modelType}`}
                label="Sem RMV"
                value="without-rmv"
                checked={!rmv}
                onChange={() => handleRMV(false)}
              />
            </div>
          </section>
        </>
      )}
    </div>
  );
}
